import { useState, useEffect, useRef } from 'react'
import { Zap, Key, ArrowRight, Check, Globe, Sparkles, Search, Languages, Shield, Layout, Bell, Link2, Target, ChevronDown, ChevronUp, Wand2, Brain, Gem, HelpCircle, ExternalLink, Image, AlertTriangle, Rocket, Coffee, Settings, Palette, Layers, Shuffle, ImagePlus, Loader2 } from 'lucide-react'
import { getApiKey, setApiKey } from '@services/llm'
import { generateImage } from '@services/imagen'
import { getApiKey as getPerplexityKey, setApiKey as setPerplexityKey } from '@services/perplexity'
import { initDemoCredits } from '@services/stripe'
import { useStore } from '@store/useStore'
import GitHubConnect from './GitHubConnect'
import { requestNotificationPermission, isNotificationSupported } from '@services/notifications'

// CMS Pattern options for anti-footprint PBN
// Market share data: themeisle.com, w3techs.com (Dec 2025)
const CMS_PATTERNS = [
  { id: 'wordpress', name: 'WordPress', desc: '43% du web', share: 43 },
  { id: 'joomla', name: 'Joomla', desc: '2% du web', share: 2 },
  { id: 'drupal', name: 'Drupal', desc: '1.1% du web', share: 1.1 },
  { id: 'typo3', name: 'TYPO3', desc: '0.6% (DACH)', share: 0.6 },
  { id: 'ghost', name: 'Ghost', desc: '0.1% (Node.js)', share: 0.1 },
  { id: 'webflow', name: 'Webflow', desc: '1.2% du web', share: 1.2 },
  { id: 'tailwind', name: 'Custom', desc: 'Code maison', share: 30 },
]

// Quality presets for generation
const QUALITY_PRESETS = [
  { id: 'economique', name: 'Economique', desc: '~0.15/site', color: 'text-emerald-400' },
  { id: 'equilibre', name: 'Equilibre', desc: '~0.50/site', color: 'text-primary-400', recommended: true },
  { id: 'qualite', name: 'Qualite', desc: '~2.00/site', color: 'text-amber-400' },
]

// Search provider options for SERP analysis
const SEARCH_PROVIDERS = [
  { id: 'auto', name: 'Auto', desc: 'Meilleur dispo' },
  { id: 'perplexity', name: 'Perplexity', desc: 'Sources top' },
  { id: 'multi', name: 'Multi', desc: 'Rotation anti-footprint' },
]

// Site size presets (number of pages to generate)
const SITE_SIZE_PRESETS = [
  { id: 'micro', name: 'Micro', pages: 5, desc: 'Landing page', icon: '📄' },
  { id: 'small', name: 'Petit', pages: 8, desc: 'Site vitrine', icon: '🏠' },
  { id: 'medium', name: 'Moyen', pages: 15, desc: 'PME locale', icon: '🏢', recommended: true },
  { id: 'large', name: 'Grand', pages: 25, desc: 'Site complet', icon: '🏛️' },
  { id: 'xl', name: 'XL', pages: 40, desc: 'PBN serieux', icon: '🚀' },
  { id: 'max', name: 'Max', pages: 50, desc: 'Cocon massif', icon: '🌐' },
]

// Model options per provider
const MODEL_OPTIONS = {
  claude: [
    { id: 'auto', name: 'Auto', desc: 'Sonnet 4.5 par defaut' },
    { id: 'sonnet-4.5', name: 'Sonnet 4.5', desc: 'Le + recent & malin' },
    { id: 'opus-4.5', name: 'Opus 4.5', desc: 'Le + creatif' },
    { id: 'haiku-3.5', name: 'Haiku 3.5', desc: 'Ultra rapide' },
  ],
  openai: [
    { id: 'auto', name: 'Auto', desc: 'GPT-4o par defaut' },
    { id: 'gpt-4o', name: 'GPT-4o', desc: 'Multimodal' },
    { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', desc: 'Plus puissant' },
    { id: 'gpt-3.5-turbo', name: 'GPT-3.5', desc: 'Economique' },
  ],
  gemini: [
    { id: 'auto', name: 'Auto', desc: 'Flash par defaut' },
    { id: 'gemini-2.0-flash', name: '2.0 Flash', desc: 'Rapide' },
    { id: 'gemini-1.5-pro', name: '1.5 Pro', desc: 'Plus precis' },
  ],
}

// Provider choices with human-friendly descriptions
const PROVIDERS = [
  {
    id: 'claude',
    name: 'Claude',
    tagline: 'Le plus creatif',
    description: 'Excellent pour le contenu redactionnel',
    icon: Brain,
    color: 'from-orange-500 to-amber-500',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    placeholder: 'sk-ant-api03-...',
    keyUrl: 'https://console.anthropic.com/settings/keys',
    instructions: [
      'Allez sur console.anthropic.com',
      'Creez un compte gratuit',
      'Cliquez sur "Create Key"',
      'Copiez la cle secrete',
    ],
    needsGeminiForImages: true,
  },
  {
    id: 'openai',
    name: 'ChatGPT',
    tagline: 'Le plus connu',
    description: 'Le classique, fiable et rapide',
    icon: Sparkles,
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    placeholder: 'sk-proj-...',
    keyUrl: 'https://platform.openai.com/api-keys',
    instructions: [
      'Allez sur platform.openai.com',
      'Connectez-vous ou creez un compte',
      'Menu API keys > Create new key',
      'Copiez la cle secrete',
    ],
    needsGeminiForImages: true,
  },
  {
    id: 'gemini',
    name: 'Gemini',
    tagline: 'Texte + Images',
    description: 'Seul provider qui genere aussi les visuels',
    icon: Gem,
    color: 'from-blue-500 to-indigo-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    placeholder: 'AIzaSy...',
    keyUrl: 'https://aistudio.google.com/app/apikey',
    instructions: [
      'Allez sur aistudio.google.com',
      'Connectez-vous avec Google',
      'Cliquez sur "Get API key"',
      'Copiez la cle',
    ],
    needsGeminiForImages: false,
  },
]

const LANGUAGES = [
  { code: 'fr', label: 'Francais', flag: 'FR' },
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'es', label: 'Espanol', flag: 'ES' },
  { code: 'de', label: 'Deutsch', flag: 'DE' },
  { code: 'it', label: 'Italiano', flag: 'IT' },
  { code: 'pt', label: 'Portugues', flag: 'PT' },
]

// Network nodes canvas animation (simplified)
function NetworkCanvas() {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    let animationId
    let particles = []

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener('resize', resize)

    const particleCount = 30
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        radius: 1 + Math.random() * 1.5,
      })
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      particles.forEach((p, i) => {
        p.x += p.vx
        p.y += p.vy

        if (p.x < 0 || p.x > canvas.width) p.vx *= -1
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1

        ctx.beginPath()
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(99, 102, 241, 0.3)'
        ctx.fill()

        particles.slice(i + 1).forEach(p2 => {
          const dx = p.x - p2.x
          const dy = p.y - p2.y
          const dist = Math.sqrt(dx * dx + dy * dy)

          if (dist < 120) {
            ctx.beginPath()
            ctx.moveTo(p.x, p.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.strokeStyle = `rgba(99, 102, 241, ${0.08 * (1 - dist / 120)})`
            ctx.lineWidth = 0.5
            ctx.stroke()
          }
        })
      })

      animationId = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resize)
      cancelAnimationFrame(animationId)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ opacity: 0.3 }}
    />
  )
}

export default function Onboarding({ onComplete }) {
  const { setSettings } = useStore()

  // Check if we have a saved provider to skip step 0
  const savedProvider = localStorage.getItem('emd_provider')
  const [step, setStep] = useState(savedProvider ? 1 : 0)

  // Step 1: Provider choice (persisted)
  const [selectedProvider, setSelectedProvider] = useState(savedProvider || null)

  // Step 2: API key
  const [apiKey, setApiKeyValue] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [advancedKeys, setAdvancedKeys] = useState({
    perplexity: getPerplexityKey() || '',
    // Secondary providers
    claude: getApiKey('claude') || '',
    openai: getApiKey('openai') || '',
    gemini: getApiKey('gemini') || '',
  })

  // Step 3: Launch config (all persisted in localStorage)
  const [language, setLanguage] = useState(localStorage.getItem('emd_language') || 'fr')
  const [cmsPattern, setCmsPattern] = useState(localStorage.getItem('emd_cms_pattern') || 'wordpress')
  const [qualityPreset, setQualityPreset] = useState(localStorage.getItem('emd_quality_preset') || 'equilibre')
  const [domain, setDomainInput] = useState('')
  const [showConfig, setShowConfig] = useState(false)

  // Expert mode options (also persisted)
  const [expertMode, setExpertMode] = useState(localStorage.getItem('emd_expert_mode') === 'true')
  const [searchProvider, setSearchProvider] = useState(localStorage.getItem('emd_search_provider') || 'auto')
  const [useDualModel, setUseDualModel] = useState(localStorage.getItem('emd_dual_model') === 'true')
  const [targetSite, setTargetSite] = useState(localStorage.getItem('emd_target_site') || '')
  const [resourceLinks, setResourceLinks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('emd_resource_links') || '[]')
    } catch { return [] }
  })
  const [antiFootprint, setAntiFootprint] = useState(localStorage.getItem('emd_anti_footprint') !== 'false') // default true
  const [siteSize, setSiteSize] = useState(localStorage.getItem('emd_site_size') || 'medium')
  const [temperature, setTemperature] = useState(parseFloat(localStorage.getItem('emd_temperature')) || 0.8)
  const [thinkingModel, setThinkingModel] = useState(localStorage.getItem('emd_thinking_model') || 'sonnet-4.5')
  const [writingModel, setWritingModel] = useState(localStorage.getItem('emd_writing_model') || 'opus-4.5')

  // Image test state
  const [imageTestLoading, setImageTestLoading] = useState(false)
  const [imageTestResult, setImageTestResult] = useState(null) // { url, provider, error }

  const [saving, setSaving] = useState(false)
  const [isLaunching, setIsLaunching] = useState(false)

  // Auto-save all settings when they change
  useEffect(() => {
    localStorage.setItem('emd_language', language)
  }, [language])

  useEffect(() => {
    localStorage.setItem('emd_cms_pattern', cmsPattern)
  }, [cmsPattern])

  useEffect(() => {
    localStorage.setItem('emd_quality_preset', qualityPreset)
  }, [qualityPreset])

  useEffect(() => {
    localStorage.setItem('emd_expert_mode', expertMode.toString())
  }, [expertMode])

  useEffect(() => {
    localStorage.setItem('emd_search_provider', searchProvider)
  }, [searchProvider])

  useEffect(() => {
    localStorage.setItem('emd_dual_model', useDualModel.toString())
  }, [useDualModel])

  useEffect(() => {
    localStorage.setItem('emd_anti_footprint', antiFootprint.toString())
  }, [antiFootprint])

  useEffect(() => {
    localStorage.setItem('emd_site_size', siteSize)
  }, [siteSize])

  useEffect(() => {
    if (targetSite.trim()) {
      localStorage.setItem('emd_target_site', targetSite.trim())
    }
  }, [targetSite])

  useEffect(() => {
    localStorage.setItem('emd_resource_links', JSON.stringify(resourceLinks.filter(r => r.trim())))
  }, [resourceLinks])

  useEffect(() => {
    localStorage.setItem('emd_temperature', temperature.toString())
  }, [temperature])

  useEffect(() => {
    localStorage.setItem('emd_thinking_model', thinkingModel)
  }, [thinkingModel])

  useEffect(() => {
    localStorage.setItem('emd_writing_model', writingModel)
  }, [writingModel])

  // Load existing key for selected provider
  useEffect(() => {
    if (selectedProvider) {
      const existingKey = getApiKey(selectedProvider) || ''
      setApiKeyValue(existingKey)
    }
  }, [selectedProvider])

  const providerInfo = PROVIDERS.find(p => p.id === selectedProvider)

  const handleSaveAndContinue = () => {
    setSaving(true)

    // Save main provider key
    if (selectedProvider && apiKey.trim()) {
      setApiKey(selectedProvider, apiKey.trim())
    }

    // Save advanced keys if any
    if (advancedKeys.perplexity.trim()) {
      setPerplexityKey(advancedKeys.perplexity.trim())
    }
    // Save other provider keys from advanced
    Object.entries(advancedKeys).forEach(([provider, key]) => {
      if (provider !== 'perplexity' && key.trim() && provider !== selectedProvider) {
        setApiKey(provider, key.trim())
      }
    })

    // Save preferences (including provider for persistence)
    localStorage.setItem('emd_provider', selectedProvider)
    localStorage.setItem('emd_language', language)
    localStorage.setItem('emd_cms_pattern', cmsPattern)
    setSettings({ cmsPattern })
    initDemoCredits()

    setTimeout(() => {
      setSaving(false)
      setStep(2)
    }, 400)
  }

  const handleLaunch = () => {
    if (!domain.trim() || isLaunching) return

    // Start the rocket animation
    setIsLaunching(true)

    // After animation completes, do the actual completion
    setTimeout(() => {
      // Save all config to localStorage for persistence
      localStorage.setItem('emd_onboarded', 'true')
      localStorage.setItem('emd_provider', selectedProvider)
      localStorage.setItem('emd_language', language)
      localStorage.setItem('emd_cms_pattern', cmsPattern)
      localStorage.setItem('emd_quality_preset', qualityPreset)
      localStorage.setItem('emd_expert_mode', expertMode.toString())
      localStorage.setItem('emd_search_provider', searchProvider)
      localStorage.setItem('emd_dual_model', useDualModel.toString())
      localStorage.setItem('emd_anti_footprint', antiFootprint.toString())
      localStorage.setItem('emd_site_size', siteSize)
      localStorage.setItem('emd_temperature', temperature.toString())
      localStorage.setItem('emd_thinking_model', thinkingModel)
      localStorage.setItem('emd_writing_model', writingModel)
      if (targetSite.trim()) {
        localStorage.setItem('emd_target_site', targetSite.trim())
      } else {
        localStorage.removeItem('emd_target_site')
      }
      // Save resource links for webography
      const cleanResourceLinks = resourceLinks.filter(r => r.trim())
      localStorage.setItem('emd_resource_links', JSON.stringify(cleanResourceLinks))
      // Get max pages from preset and save to localStorage
      const siteSizePreset = SITE_SIZE_PRESETS.find(s => s.id === siteSize)
      const maxPages = siteSizePreset?.pages || 15
      localStorage.setItem('emd_max_pages', maxPages.toString())
      setSettings({
        cmsPattern,
        qualityPreset,
        provider: selectedProvider,
        searchProvider,
        useDualModel,
        antiFootprint,
        siteSize,
        maxPages,
        temperature,
        thinkingModel,
        writingModel,
        targetSite: targetSite.trim() || null,
        resourceLinks: cleanResourceLinks,
      })
      onComplete(domain.trim() || null)
    }, 1200) // Animation duration
  }

  const handleAdvancedKeyChange = (provider, value) => {
    setAdvancedKeys(prev => ({ ...prev, [provider]: value }))
  }

  // Test image generation with available provider
  const handleImageTest = async () => {
    setImageTestLoading(true)
    setImageTestResult(null)

    // Check which provider is available
    const keys = JSON.parse(localStorage.getItem('emd_api_keys') || '{}')
    const provider = keys.openai ? 'dalle' : keys.gemini ? 'gemini' : null

    if (!provider) {
      setImageTestResult({ error: 'Aucune cle API image (OpenAI ou Gemini)' })
      setImageTestLoading(false)
      return
    }

    try {
      console.log(`🧪 Testing image generation with ${provider}...`)
      const result = await generateImage(
        'A professional French artisan at work, clean modern workspace, natural lighting, European setting',
        { provider, width: 1024, height: 1024 }
      )
      console.log('🧪 Image test success!', result.provider)
      setImageTestResult({ url: result.url, provider: result.provider })
    } catch (err) {
      console.error('🧪 Image test failed:', err)
      setImageTestResult({ error: err.message })
    } finally {
      setImageTestLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-950 flex items-center justify-center z-50 overflow-y-auto py-8">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <NetworkCanvas />
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary-500/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent-500/10 rounded-full blur-[128px]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(2,6,23,0.7)_70%)]" />
      </div>

      <div className="max-w-md w-full mx-4 relative z-10">
        {/* Progress */}
        <div className="flex justify-center gap-2 mb-6">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step
                  ? 'w-8 bg-primary-500'
                  : i < step
                    ? 'w-4 bg-primary-400'
                    : 'w-4 bg-slate-700'
              }`}
            />
          ))}
        </div>

        {/* Card with animated glow */}
        <div className="relative">
          {/* Animated glow border */}
          <style>{`
            @keyframes glow-spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
          <div className="absolute -inset-1 rounded-2xl overflow-hidden pointer-events-none">
            <div
              className="absolute -inset-8 transition-all duration-1000"
              style={{
                background: step === 0
                  ? 'conic-gradient(from 180deg, transparent 0%, transparent 30%, #06b6d4 45%, #3b82f6 55%, #6366f1 65%, transparent 80%, transparent 100%)'
                  : step === 1
                    ? 'conic-gradient(from 180deg, transparent 0%, transparent 30%, #10b981 45%, #14b8a6 55%, #06b6d4 65%, transparent 80%, transparent 100%)'
                    : 'conic-gradient(from 180deg, transparent 0%, transparent 30%, #6366f1 45%, #a855f7 55%, #ec4899 65%, transparent 80%, transparent 100%)',
                animation: 'glow-spin 8s linear infinite',
                filter: 'blur(12px)',
                opacity: 0.6,
              }}
            />
          </div>
          <div className="relative bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 shadow-2xl">

          {/* ============ STEP 0: Choose Provider ============ */}
          {step === 0 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mx-auto mb-4">
                  <Wand2 className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-xl font-bold text-white mb-1">Choisissez votre IA</h1>
                <p className="text-sm text-slate-400">C'est elle qui va ecrire votre site</p>
              </div>

              {/* Provider cards */}
              <div className="space-y-3 mb-6">
                {PROVIDERS.map(provider => {
                  const Icon = provider.icon
                  const isSelected = selectedProvider === provider.id
                  return (
                    <button
                      key={provider.id}
                      onClick={() => {
                        setSelectedProvider(provider.id)
                        localStorage.setItem('emd_provider', provider.id)
                      }}
                      className={`w-full p-4 rounded-xl border text-left transition-all ${
                        isSelected
                          ? `${provider.borderColor} ${provider.bgColor} border-2`
                          : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${provider.color} flex items-center justify-center`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white">{provider.name}</span>
                            <span className="text-xs px-2 py-0.5 bg-slate-700/50 rounded-full text-slate-300">
                              {provider.tagline}
                            </span>
                          </div>
                          <p className="text-sm text-slate-400 mt-0.5">{provider.description}</p>
                        </div>
                        {isSelected && (
                          <Check className="w-5 h-5 text-primary-400" />
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>

              <p className="text-xs text-slate-500 text-center mb-4">
                Vous pourrez en ajouter d'autres plus tard dans les parametres
              </p>

              <button
                onClick={() => selectedProvider && setStep(1)}
                disabled={!selectedProvider}
                className="w-full py-3 bg-primary-600 hover:bg-primary-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
              >
                Continuer <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* ============ STEP 1: Enter API Key ============ */}
          {step === 1 && providerInfo && (
            <div>
              <div className="text-center mb-6">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${providerInfo.color} flex items-center justify-center mx-auto mb-4`}>
                  <providerInfo.icon className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-xl font-bold text-white mb-1">Connectez {providerInfo.name}</h1>
                <p className="text-sm text-slate-400">Entrez votre cle API</p>
              </div>

              {/* Instructions */}
              <div className="bg-slate-800/50 rounded-xl p-4 mb-4 border border-slate-700/50">
                <div className="flex items-center gap-2 mb-3">
                  <HelpCircle className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-300">Comment obtenir la cle ?</span>
                </div>
                <ol className="space-y-1.5 text-sm text-slate-400">
                  {providerInfo.instructions.map((instruction, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-primary-400 font-medium">{i + 1}.</span>
                      {instruction}
                    </li>
                  ))}
                </ol>
                <a
                  href={providerInfo.keyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-sm text-primary-400 hover:text-primary-300 font-medium"
                >
                  Ouvrir {providerInfo.name} <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>

              {/* API Key input */}
              <div className="mb-4">
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKeyValue(e.target.value)}
                  placeholder={providerInfo.placeholder}
                  className="w-full px-4 py-3.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none focus:border-primary-500 text-white placeholder-slate-500 font-mono text-sm"
                  autoFocus
                />
              </div>

              {/* Warning: need Gemini for images */}
              {providerInfo.needsGeminiForImages && (
                <div className="mb-4 p-3 bg-amber-500/10 rounded-xl border border-amber-500/30">
                  <div className="flex items-start gap-2">
                    <Image className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-amber-200">
                        <strong>Images</strong> : ajoutez Gemini dans "Options avancees" pour generer les visuels du site.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Advanced options toggle */}
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full flex items-center justify-between py-2 text-sm text-slate-400 hover:text-slate-300 transition-all"
              >
                <span>Options avancees (recherche, autres IA...)</span>
                {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {/* Advanced options panel */}
              {showAdvanced && (
                <div className="mt-3 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50 space-y-4">
                  {/* Perplexity for search */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs text-slate-400 flex items-center gap-1.5">
                        <Search className="w-3.5 h-3.5" />
                        Perplexity (recherche web)
                      </label>
                      <a
                        href="https://www.perplexity.ai/settings/api"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary-400 hover:text-primary-300"
                      >
                        Obtenir
                      </a>
                    </div>
                    <input
                      type="password"
                      value={advancedKeys.perplexity}
                      onChange={(e) => handleAdvancedKeyChange('perplexity', e.target.value)}
                      placeholder="pplx-..."
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-primary-500 text-white placeholder-slate-500 font-mono"
                    />
                    <p className="text-xs text-slate-500 mt-1">Ameliore l'analyse concurrentielle</p>
                  </div>

                  {/* Other providers - Gemini first if needed for images */}
                  {PROVIDERS
                    .filter(p => p.id !== selectedProvider)
                    .sort((a, b) => {
                      // Put Gemini first if main provider needs it for images
                      if (providerInfo?.needsGeminiForImages) {
                        if (a.id === 'gemini') return -1
                        if (b.id === 'gemini') return 1
                      }
                      return 0
                    })
                    .map(provider => {
                      const isGeminiNeeded = provider.id === 'gemini' && providerInfo?.needsGeminiForImages
                      return (
                        <div key={provider.id} className={isGeminiNeeded ? 'p-3 -mx-3 bg-amber-500/5 rounded-lg border border-amber-500/20' : ''}>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className={`text-xs flex items-center gap-1.5 ${isGeminiNeeded ? 'text-amber-300 font-medium' : 'text-slate-400'}`}>
                              {isGeminiNeeded && <Image className="w-3.5 h-3.5" />}
                              {provider.name}
                              {isGeminiNeeded && <span className="text-amber-400/70">(pour les images)</span>}
                            </label>
                            <a
                              href={provider.keyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary-400 hover:text-primary-300"
                            >
                              Obtenir
                            </a>
                          </div>
                          <input
                            type="password"
                            value={advancedKeys[provider.id]}
                            onChange={(e) => handleAdvancedKeyChange(provider.id, e.target.value)}
                            placeholder={provider.placeholder}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-primary-500 text-white placeholder-slate-500 font-mono ${
                              isGeminiNeeded ? 'bg-slate-800/80 border-amber-500/30' : 'bg-slate-800 border-slate-700'
                            }`}
                          />
                        </div>
                      )
                    })}

                  {/* GitHub */}
                  <div className="pt-2 border-t border-slate-700/50">
                    <label className="text-xs text-slate-400 mb-2 block">GitHub (deploiement)</label>
                    <GitHubConnect compact />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-slate-500 my-4">
                <Shield className="w-3.5 h-3.5 text-emerald-400" />
                <span>Stockage local uniquement, jamais envoye a nos serveurs</span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setStep(0)}
                  className="px-4 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl transition-all text-slate-300"
                >
                  Retour
                </button>
                <button
                  onClick={handleSaveAndContinue}
                  disabled={!apiKey.trim() || saving}
                  className="flex-1 py-3 bg-primary-600 hover:bg-primary-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                >
                  {saving ? 'Enregistrement...' : 'Continuer'}
                  {!saving && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {/* ============ STEP 2: Launch! ============ */}
          {step === 2 && (
            <div>
              {/* Fun header */}
              <div className="text-center mb-5">
                <div className="relative w-14 h-14 mx-auto mb-3">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl animate-pulse opacity-50" />
                  <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                    <Rocket className="w-7 h-7 text-white" />
                  </div>
                </div>
                <h1 className="text-xl font-bold text-white mb-1">Pret au decollage ?</h1>
                <p className="text-sm text-slate-400">
                  <Coffee className="w-3.5 h-3.5 inline text-amber-400 mr-1" />
                  Temps d'un cafe
                </p>
              </div>

              {/* Domain Input - THE star */}
              <div className="mb-4">
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="text"
                    value={domain}
                    onChange={(e) => setDomainInput(e.target.value)}
                    placeholder="plombier-paris-15.fr"
                    className="w-full pl-12 pr-4 py-4 bg-slate-800 border-2 border-slate-700 rounded-xl focus:outline-none focus:border-primary-500 text-white placeholder-slate-500 text-lg font-medium"
                    autoFocus
                  />
                </div>
              </div>

              {/* Config summary - collapsed by default */}
              <div className="mb-4">
                <button
                  onClick={() => setShowConfig(!showConfig)}
                  className="relative w-full flex items-center justify-between p-3 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-slate-600 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Settings className="w-4 h-4 text-slate-400" />
                    <div className="text-left">
                      <div className="text-sm text-slate-300">
                        {PROVIDERS.find(p => p.id === selectedProvider)?.name} · {LANGUAGES.find(l => l.code === language)?.label} · {CMS_PATTERNS.find(c => c.id === cmsPattern)?.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {QUALITY_PRESETS.find(q => q.id === qualityPreset)?.name}
                        {expertMode && (
                          <span className="ml-1 text-primary-400">
                            · {SITE_SIZE_PRESETS.find(s => s.id === siteSize)?.pages}p
                            · {SEARCH_PROVIDERS.find(s => s.id === searchProvider)?.name}
                            {useDualModel && ' · Dual'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showConfig ? 'rotate-180' : ''}`} />
                </button>

                {/* Expanded config */}
                {showConfig && (
                  <div className="mt-3 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50 space-y-4">
                    {/* Language */}
                    <div>
                      <label className="text-xs text-slate-400 mb-1.5 block flex items-center gap-1.5">
                        <Languages className="w-3.5 h-3.5" /> Langue du contenu
                      </label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {LANGUAGES.map(lang => (
                          <button
                            key={lang.code}
                            onClick={() => setLanguage(lang.code)}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                              language === lang.code
                                ? 'bg-primary-600 text-white'
                                : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                            }`}
                          >
                            {lang.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* CMS Style */}
                    <div>
                      <label className="text-xs text-slate-400 mb-1.5 block flex items-center gap-1.5">
                        <Palette className="w-3.5 h-3.5" /> Empreinte code
                      </label>
                      <div className="grid grid-cols-2 gap-1.5">
                        {CMS_PATTERNS.map(cms => (
                          <button
                            key={cms.id}
                            onClick={() => setCmsPattern(cms.id)}
                            className={`py-2 px-3 rounded-lg text-left transition-all ${
                              cmsPattern === cms.id
                                ? 'bg-accent-600 text-white'
                                : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                            }`}
                          >
                            <div className="text-sm font-medium">{cms.name}</div>
                            <div className="text-xs opacity-70">{cms.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Quality preset */}
                    <div>
                      <label className="text-xs text-slate-400 mb-1.5 block flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" /> Qualite de generation
                      </label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {QUALITY_PRESETS.map(preset => (
                          <button
                            key={preset.id}
                            onClick={() => setQualityPreset(preset.id)}
                            className={`py-2 px-2 rounded-lg text-center transition-all ${
                              qualityPreset === preset.id
                                ? 'bg-slate-700 ring-2 ring-primary-500'
                                : 'bg-slate-700/50 hover:bg-slate-700'
                            }`}
                          >
                            <div className={`text-sm font-medium ${preset.color}`}>{preset.name}</div>
                            <div className="text-xs text-slate-500">{preset.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Expert mode toggle */}
                    <div className="pt-3 border-t border-slate-700/50">
                      <button
                        onClick={() => setExpertMode(!expertMode)}
                        className="w-full flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Layers className="w-4 h-4 text-primary-400" />
                          <span className="text-sm text-slate-300">Mode Expert SEO</span>
                        </div>
                        <div className={`w-10 h-5 rounded-full transition-all flex items-center ${
                          expertMode ? 'bg-primary-500' : 'bg-slate-600'
                        }`}>
                          <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${
                            expertMode ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </div>
                      </button>
                    </div>

                    {/* Expert options */}
                    {expertMode && (() => {
                      // Calculate availability based on entered keys
                      const hasPerplexity = !!advancedKeys.perplexity.trim()
                      const hasClaude = selectedProvider === 'claude' || !!advancedKeys.claude.trim()
                      const hasOpenAI = selectedProvider === 'openai' || !!advancedKeys.openai.trim()
                      const hasGemini = selectedProvider === 'gemini' || !!advancedKeys.gemini.trim()

                      // Count available search providers (main provider can search too)
                      const searchCount = [hasPerplexity, hasClaude, hasOpenAI, hasGemini].filter(Boolean).length

                      const isSearchAvailable = (id) => {
                        if (id === 'auto') return true // Always available
                        if (id === 'perplexity') return hasPerplexity
                        if (id === 'multi') return searchCount >= 2
                        return true
                      }

                      // Dual model only works with Claude
                      const canUseDualModel = hasClaude

                      return (
                        <div className="space-y-4 pt-3 border-t border-primary-500/30 bg-primary-500/5 -mx-4 px-4 pb-1 rounded-b-lg">
                          {/* Temperature slider */}
                          <div>
                            <label className="text-xs text-primary-300 mb-1.5 block flex items-center gap-1.5">
                              <Sparkles className="w-3.5 h-3.5" /> Creativite (temperature)
                            </label>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-slate-500">Precis</span>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                value={temperature}
                                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                                className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
                              />
                              <span className="text-xs text-slate-500">Creatif</span>
                              <span className="text-xs text-primary-400 font-mono w-8">{temperature}</span>
                            </div>
                            <p className="text-xs text-slate-600 mt-1">
                              Plus eleve = designs plus varies, textes plus originaux
                            </p>
                          </div>

                          {/* Model selectors - Thinking & Writing */}
                          <div className="grid grid-cols-2 gap-3">
                            {/* Thinking model */}
                            <div>
                              <label className="text-xs text-primary-300 mb-1.5 block flex items-center gap-1.5">
                                <Brain className="w-3.5 h-3.5" /> Pensee (agents)
                              </label>
                              <div className="space-y-1">
                                {(MODEL_OPTIONS[selectedProvider] || MODEL_OPTIONS.claude).filter(m => m.id !== 'auto').map(model => (
                                  <button
                                    key={model.id}
                                    onClick={() => setThinkingModel(model.id)}
                                    className={`w-full py-1.5 px-2 rounded-lg text-left transition-all ${
                                      thinkingModel === model.id
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                                    }`}
                                  >
                                    <div className="text-xs font-medium">{model.name}</div>
                                  </button>
                                ))}
                              </div>
                              <p className="text-xs text-slate-600 mt-1">Analyse, strategie</p>
                            </div>

                            {/* Writing model */}
                            <div>
                              <label className="text-xs text-primary-300 mb-1.5 block flex items-center gap-1.5">
                                <Wand2 className="w-3.5 h-3.5" /> Ecriture (contenu)
                              </label>
                              <div className="space-y-1">
                                {(MODEL_OPTIONS[selectedProvider] || MODEL_OPTIONS.claude).filter(m => m.id !== 'auto').map(model => (
                                  <button
                                    key={model.id}
                                    onClick={() => setWritingModel(model.id)}
                                    className={`w-full py-1.5 px-2 rounded-lg text-left transition-all ${
                                      writingModel === model.id
                                        ? 'bg-accent-600 text-white'
                                        : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                                    }`}
                                  >
                                    <div className="text-xs font-medium">{model.name}</div>
                                  </button>
                                ))}
                              </div>
                              <p className="text-xs text-slate-600 mt-1">HTML, textes finaux</p>
                            </div>
                          </div>

                          {/* Search provider */}
                          <div>
                            <label className="text-xs text-primary-300 mb-1.5 block flex items-center gap-1.5">
                              <Search className="w-3.5 h-3.5" /> Moteur de recherche SERP
                            </label>
                            <div className="grid grid-cols-3 gap-1.5">
                              {SEARCH_PROVIDERS.map(sp => {
                                const available = isSearchAvailable(sp.id)
                                return (
                                  <button
                                    key={sp.id}
                                    onClick={() => available && setSearchProvider(sp.id)}
                                    disabled={!available}
                                    className={`py-2 px-2 rounded-lg text-center transition-all ${
                                      !available
                                        ? 'bg-slate-800/30 text-slate-600 cursor-not-allowed'
                                        : searchProvider === sp.id
                                          ? 'bg-primary-600 text-white'
                                          : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                                    }`}
                                    title={!available ? (sp.id === 'perplexity' ? 'Cle Perplexity requise' : 'Min. 2 providers') : ''}
                                  >
                                    <div className="text-sm font-medium">{sp.name}</div>
                                    <div className="text-xs opacity-70">
                                      {!available ? 'Non dispo' : sp.desc}
                                    </div>
                                  </button>
                                )
                              })}
                            </div>
                          </div>

                          {/* Anti-footprint toggle */}
                          <div>
                            <button
                              onClick={() => setAntiFootprint(!antiFootprint)}
                              className="w-full flex items-center justify-between p-3 rounded-lg border bg-slate-800/50 border-slate-700/50"
                            >
                              <div className="text-left">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-4 h-4 text-emerald-400" />
                                  <span className="text-sm font-medium text-slate-200">
                                    Anti-Footprint++
                                  </span>
                                </div>
                                <p className="text-xs mt-0.5 text-slate-500">
                                  CDN fake, pixels GA/FB, cookie banner
                                </p>
                              </div>
                              <div className={`w-10 h-5 rounded-full transition-all flex items-center ${
                                antiFootprint ? 'bg-emerald-500' : 'bg-slate-600'
                              }`}>
                                <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                  antiFootprint ? 'translate-x-5' : 'translate-x-0.5'
                                }`} />
                              </div>
                            </button>
                          </div>

                          {/* Site size selector */}
                          <div>
                            <label className="text-xs text-primary-300 mb-1.5 block flex items-center gap-1.5">
                              <Layout className="w-3.5 h-3.5" /> Taille du site (nb pages)
                            </label>
                            <div className="grid grid-cols-3 gap-1.5">
                              {SITE_SIZE_PRESETS.map(preset => (
                                <button
                                  key={preset.id}
                                  onClick={() => setSiteSize(preset.id)}
                                  className={`py-2 px-2 rounded-lg text-center transition-all relative ${
                                    siteSize === preset.id
                                      ? 'bg-primary-600 text-white ring-2 ring-primary-400'
                                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                                  }`}
                                >
                                  {preset.recommended && siteSize !== preset.id && (
                                    <span className="absolute -top-1 -right-1 text-[10px] bg-amber-500 text-white px-1 rounded">
                                      rec
                                    </span>
                                  )}
                                  <div className="text-sm font-medium">
                                    <span className="mr-1">{preset.icon}</span>
                                    {preset.pages}
                                  </div>
                                  <div className="text-xs opacity-70">{preset.name}</div>
                                </button>
                              ))}
                            </div>
                            <p className="text-xs text-slate-600 mt-1.5">
                              {SITE_SIZE_PRESETS.find(s => s.id === siteSize)?.desc} — {SITE_SIZE_PRESETS.find(s => s.id === siteSize)?.pages} pages max
                            </p>
                          </div>

                          {/* Target site to promote */}
                          <div>
                            <label className="text-xs text-primary-300 mb-1.5 block flex items-center gap-1.5">
                              <Target className="w-3.5 h-3.5" /> Site a pousser (optionnel)
                            </label>
                            <input
                              type="text"
                              value={targetSite}
                              onChange={(e) => setTargetSite(e.target.value)}
                              placeholder="mon-site-principal.fr"
                              className="w-full px-3 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm focus:outline-none focus:border-primary-500 text-white placeholder-slate-500"
                            />
                            <p className="text-xs text-slate-600 mt-1">
                              L'EMD fera des liens vers ce site
                            </p>
                          </div>

                          {/* Resource links for webography */}
                          <div>
                            <label className="text-xs text-primary-300 mb-1.5 block flex items-center gap-1.5">
                              <Link2 className="w-3.5 h-3.5" /> Ressources webographie (optionnel)
                            </label>
                            <div className="space-y-2">
                              {[0, 1, 2, 3, 4].map((idx) => (
                                <input
                                  key={idx}
                                  type="text"
                                  value={resourceLinks[idx] || ''}
                                  onChange={(e) => {
                                    const newLinks = [...resourceLinks]
                                    newLinks[idx] = e.target.value
                                    setResourceLinks(newLinks)
                                  }}
                                  placeholder={`ressource-${idx + 1}.fr (optionnel)`}
                                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm focus:outline-none focus:border-primary-500 text-white placeholder-slate-500"
                                />
                              ))}
                            </div>
                            <p className="text-xs text-slate-600 mt-1">
                              Jusqu'a 5 liens distribues dans les webographies des pages
                            </p>
                          </div>

                          {/* Image generation test */}
                          <div className="pt-2 border-t border-slate-700/30">
                            <label className="text-xs text-primary-300 mb-1.5 block flex items-center gap-1.5">
                              <ImagePlus className="w-3.5 h-3.5" /> Test generation image
                            </label>
                            <button
                              onClick={handleImageTest}
                              disabled={imageTestLoading}
                              className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                                imageTestLoading
                                  ? 'bg-slate-700 text-slate-400 cursor-wait'
                                  : 'bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white'
                              }`}
                            >
                              {imageTestLoading ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Generation en cours...
                                </>
                              ) : (
                                <>
                                  <ImagePlus className="w-4 h-4" />
                                  Tester GPT-Image / Gemini
                                </>
                              )}
                            </button>

                            {/* Test result */}
                            {imageTestResult && (
                              <div className="mt-2">
                                {imageTestResult.error ? (
                                  <div className="p-2 bg-red-900/30 border border-red-700/50 rounded-lg text-xs text-red-300">
                                    {imageTestResult.error}
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <div className="text-xs text-emerald-400 flex items-center gap-1">
                                      <Check className="w-3 h-3" />
                                      {imageTestResult.provider === 'dalle' ? 'GPT-Image-1' : 'Gemini Imagen'} OK!
                                    </div>
                                    <img
                                      src={imageTestResult.url}
                                      alt="Test image"
                                      className="w-full h-32 object-cover rounded-lg border border-slate-700"
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                            <p className="text-xs text-slate-600 mt-1">
                              Verifie que ta cle API image fonctionne
                            </p>
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                )}
              </div>

              {/* THE button */}
              <button
                onClick={handleLaunch}
                disabled={!domain.trim() || isLaunching}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 ${
                  domain.trim() && !isLaunching
                    ? 'bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-400 hover:to-accent-400 shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 hover:scale-[1.02]'
                    : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                }`}
              >
                {isLaunching ? (
                  'Decollage...'
                ) : domain.trim() ? (
                  <>
                    <Rocket className="w-5 h-5" />
                    Lancer la magie
                  </>
                ) : (
                  'Entrez un domaine'
                )}
              </button>

              {/* Reassurance */}
              <p className="text-xs text-slate-500 text-center mt-3">
                3-5 minutes · cafe recommande
              </p>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Rocket launch animation overlay */}
      {isLaunching && (
        <>
          <style>{`
            @keyframes rocket-launch {
              0% {
                transform: translateY(0) scale(1);
                opacity: 1;
              }
              30% {
                transform: translateY(-20px) scale(1.5);
              }
              100% {
                transform: translateY(-150vh) scale(3);
                opacity: 0.8;
              }
            }
            @keyframes smoke-puff {
              0% {
                transform: scale(0) translateY(0);
                opacity: 0.8;
              }
              100% {
                transform: scale(3) translateY(20px);
                opacity: 0;
              }
            }
            @keyframes screen-flash {
              0% { opacity: 0; }
              50% { opacity: 0.3; }
              100% { opacity: 0; }
            }
          `}</style>
          <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden">
            {/* Screen flash */}
            <div
              className="absolute inset-0 bg-white"
              style={{ animation: 'screen-flash 1.2s ease-out' }}
            />
            {/* Rocket */}
            <div
              className="absolute left-1/2 bottom-1/3 -translate-x-1/2"
              style={{ animation: 'rocket-launch 1.2s ease-in forwards' }}
            >
              <div className="relative">
                <Rocket className="w-20 h-20 text-primary-400 rotate-[-45deg]" />
                {/* Fire trail */}
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-8 h-16 bg-gradient-to-t from-transparent via-orange-500 to-yellow-400 rounded-full blur-sm animate-pulse" />
              </div>
            </div>
            {/* Smoke puffs */}
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="absolute left-1/2 bottom-1/4 -translate-x-1/2 w-16 h-16 bg-slate-400/30 rounded-full blur-xl"
                style={{
                  animation: `smoke-puff 1s ease-out ${i * 0.1}s forwards`,
                  left: `calc(50% + ${(i - 2) * 30}px)`,
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
